import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { marked } from 'marked';

/**
 * Renders chat content (markdown + occasional HTML) into safe HTML for
 * `[innerHTML]` binding. Uses `marked` for the markdown grammar and
 * Angular's DomSanitizer to strip XSS surface (event handlers,
 * `javascript:` URLs, `<script>`, etc.) before the string reaches the DOM.
 *
 * Mid-stream calls go through `formatStreamingMessage`, which only renders
 * markdown up to the last paragraph break — the tail is rendered as a
 * plain `<p>` of escaped text. This prevents marked from emitting
 * transient broken HTML for partial constructs (mid-table, half-finished
 * list, dangling `**bold`) as new deltas arrive.
 */
@Injectable({ providedIn: 'root' })
export class MarkdownService {
    constructor(private readonly _sanitizer: DomSanitizer) {
        marked.use({ gfm: true, breaks: true });
    }

    /** Final, complete message. */
    formatMessage(message: string): string {
        if (!message) {
            return '';
        }
        if (this._looksLikeHtml(message)) {
            return this._sanitize(message);
        }
        return this._sanitize(this._postProcess(this._renderMarkdown(message)));
    }

    /**
     * Mid-stream message. Markdown is only parsed up to the last `\n\n`;
     * everything after is rendered as a single escaped `<p>`.
     */
    formatStreamingMessage(message: string): string {
        if (!message) {
            return '';
        }
        if (this._looksLikeHtml(message)) {
            return this._sanitize(message);
        }

        // Inject paragraph breaks when the model has glued thoughts with
        // "…X:Capital" — the wall-of-colons failure mode you see when it
        // ignores the "one thought per paragraph" rule. Each such boundary
        // becomes a `\n\n` so the buffered-paragraph render below treats
        // them as separate prose blocks. Period+Capital with no space
        // ("…details.Let me…") gets the same treatment.
        const normalized = message
            .replace(/([a-z0-9\)\]"'])([:.])([A-Z])/g, '$1$2\n\n$3');

        const lastBreak = normalized.lastIndexOf('\n\n');
        if (lastBreak === -1) {
            return this._sanitize(`<p>${this._escapeHtml(normalized)}</p>`);
        }

        const completed = normalized.slice(0, lastBreak);
        const tail = normalized.slice(lastBreak + 2);
        const completedHtml = this._postProcess(this._renderMarkdown(completed));
        const tailHtml = tail.trim() ? `<p>${this._escapeHtml(tail)}</p>` : '';
        return this._sanitize(completedHtml + tailHtml);
    }

    /** Markdown → plain text for previews / notification snippets. */
    markdownToPlainText(message: string): string {
        if (!message) {
            return '';
        }
        return message
            .replace(/^#{1,6}\s+(.+)$/gm, '$1')
            .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
            .replace(/\*\*(.*?)\*\*/g, '$1')
            .replace(/\*([^*]+)\*/g, '$1')
            .replace(/^[-*]\s+(.+)$/gm, '$1')
            .replace(/^\d+\.\s+(.+)$/gm, '$1')
            .replace(/\s+/g, ' ')
            .trim();
    }

    private _renderMarkdown(input: string): string {
        return marked.parse(input, { async: false }) as string;
    }

    private _looksLikeHtml(message: string): boolean {
        return /<[a-z][^>]*>/i.test(message);
    }

    private _sanitize(html: string): string {
        return this._sanitizer.sanitize(SecurityContext.HTML, html) ?? '';
    }

    private _escapeHtml(s: string): string {
        return s
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }

    /**
     * Wrap tables in `.md-table-wrap` so existing SCSS can give them a
     * horizontal scroll container. Force external `<a>` to open in a new
     * tab with safe `rel`. Done as a string post-process to stay
     * compatible across `marked` versions without depending on its
     * renderer-object typings.
     */
    private _postProcess(html: string): string {
        let out = html.replace(/<table>/g, '<div class="md-table-wrap"><table>')
                      .replace(/<\/table>/g, '</table></div>');
        out = out.replace(/<a\s+href="([^"]*)"([^>]*)>/g, (_match, href, rest) => {
            const safeHref = /^(?:https?:|mailto:|tel:|\/|#)/i.test(href) ? href : '#';
            const cleaned = rest.replace(/\s*target="[^"]*"/g, '').replace(/\s*rel="[^"]*"/g, '');
            return `<a href="${safeHref}"${cleaned} target="_blank" rel="noopener noreferrer">`;
        });
        return out;
    }
}
